import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Techniques of integration — the first unit of Calculus II.
 *
 * The unit has one argument and makes it six times: **every rule for
 * differentiating is a technique for integrating, read backwards.** Substitution
 * is the chain rule; parts is the product rule; the trigonometric integrals are
 * the identities; partial fractions is the algebra of adding two fractions;
 * trigonometric substitution is all three at once. Taught as six independent
 * tricks — which is how most textbooks arrange them, one section each, with a
 * mixed exercise set at the end — the unit is a memory test, and the mixed set
 * is the part students cannot do, because nothing in the six sections was ever
 * about *choosing*.
 *
 * So each lesson here opens by naming the differentiation rule it undoes, and
 * every lesson from the second onwards begins by asking why the previous
 * technique fails on the new integral. `integration-by-parts` starts with
 * ∫x e^x dx and a failed substitution rather than with the formula;
 * `trigonometric-substitution` starts with a square root that no substitution
 * simplifies.
 *
 * `u-substitution` spends its warnings on the bookkeeping rather than the idea.
 * Choosing u is rarely the difficulty — dx is. A student who writes
 * ∫(x² + 1)⁵ dx = ∫u⁵ dx has changed the letter and nothing else, and the
 * resulting answer is wrong by a factor that depends on x. Every worked example
 * in that lesson accounts for dx explicitly, on its own line.
 *
 * `improper-integrals` is built around a wrong answer rather than a right one.
 * ∫ from −1 to 1 of dx/x evaluates, by the fundamental theorem applied without
 * looking, to ln 1 − ln 1 = 0. The integral does not exist. Nothing in the
 * arithmetic signals this, which is exactly why the check has to become a habit
 * performed before the antiderivative is written rather than after.
 *
 * `partial-fractions` teaches the cover-up method after the simultaneous
 * equations rather than instead of them. The shortcut is genuinely faster and
 * genuinely opaque, and a student who has only ever covered up a factor cannot
 * handle a repeated one — which is the next case, and the case where covering up
 * finds one constant of the two.
 */
export const INTEGRATION_TECHNIQUE_LESSONS: Lesson[] = [
  lesson({
    id: 'u-substitution-01',
    unitId: 'unit-integration-techniques',
    conceptId: 'u-substitution',
    title: 'Substitution',
    summary: 'The chain rule run backwards: spotting the inside function, and accounting for every last dx.',
    objectives: [
      'Recognise an integrand of the form f(g(x))·g′(x) and name the substitution',
      'Carry out a substitution, converting dx into du rather than dropping it',
      'Evaluate a definite integral by changing the limits, without converting back',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The theorem is only as good as your antiderivative',
        blocks: [
          text('The fundamental theorem says that to evaluate an integral you need an antiderivative. It does not say how to find one, and so far every integral you have met has been a polynomial, where reading the power rule backwards is enough.'),
          text('Here is one that is not:'),
          math('\\int 2x\\,(x^{2} + 1)^{5}\\,dx'),
          text('Expanding the bracket works — it is a degree-ten polynomial, and after twenty minutes of binomial expansion you would have the answer. Nobody does this, because the integrand is not really a polynomial in disguise. It is a *composition*, and it got that way by being differentiated.'),
          insight('Differentiate (x² + 1)⁶ and see what comes out: 6(x² + 1)⁵ · 2x. That is the integrand, times 6. So the answer is (x² + 1)⁶/6 + C, and the whole of this lesson is a systematic way of noticing that.'),
        ],
      },
      intuition: {
        title: 'The chain rule, read from right to left',
        blocks: [
          text('The chain rule says that differentiating a composition produces two factors:'),
          math('\\frac{d}{dx}\\,F(g(x)) = F\'(g(x)) \\cdot g\'(x)'),
          text('Read that equation from right to left instead. If an integrand happens to look like **a function of something, times the derivative of that something**, then it is a chain-rule output, and its antiderivative is the outer antiderivative applied to the inside:'),
          math('\\int f(g(x))\\,g\'(x)\\,dx = F(g(x)) + C \\qquad \\text{where } F\' = f'),
          text('The notation that makes this usable is to name the inside. Write u = g(x). Then du/dx = g′(x), which is rearranged — with a licence calculus takes and analysis later justifies — into'),
          math('du = g\'(x)\\,dx'),
          text('and the integral becomes'),
          math('\\int f(u)\\,du'),
          insight('The point of substitution is not that u is shorter to write. It is that the integral in u has no composition left in it at all. If your new integral is still a composition, you substituted the wrong thing.'),
          text('**The procedure**, in four steps, none of which may be skipped:'),
          list([
            'Choose u — usually the inside of a bracket, the thing under a root, the exponent, or the denominator.',
            'Differentiate it to get du = g′(x) dx.',
            'Replace *both* the f(g(x)) part and the g′(x) dx part. Every x must vanish.',
            'Integrate in u, then put g(x) back — unless the integral was definite, in which case see below.',
          ], true),
          warning('Step 3 is where this goes wrong, and it goes wrong the same way every time. Writing ∫(x² + 1)⁵ dx = ∫u⁵ dx changes the letter and nothing else: dx is not du, and the answer that follows is wrong by a factor of 2x — which is not even a constant, so no amount of adjusting afterwards can repair it. dx has to be *converted*, not carried along.', 'dx is not a decoration'),
          text('**Constants can be borrowed.** If du = 2x dx but the integral only offers x dx, that is fine — halve it:'),
          math('x\\,dx = \\tfrac{1}{2}\\,du'),
          tip('Only *constants* can be moved across the integral sign like this. If what is missing is a factor of x, the substitution has failed and you need a different one — or a different technique entirely.'),
          text('**Definite integrals: change the limits.** The limits of ∫ from 0 to 1 are values of x. Once the variable is u, they are the wrong numbers. Convert them with the same rule u = g(x):'),
          math('\\int_{a}^{b} f(g(x))\\,g\'(x)\\,dx = \\int_{g(a)}^{g(b)} f(u)\\,du'),
          warning('Change the limits *or* convert back to x at the end — never both, and never neither. Evaluating an antiderivative in u at the original x-limits is the single commonest error in this topic, and it produces a plausible number with nothing to flag it.', 'Limits belong to a variable'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              shade: { from: 0, to: 2 },
              min: -1,
              max: 3,
              label: 'y = x²',
            },
            'A definite integral is an area under a curve in the x-plane. Substituting redraws it as an area under a different curve in the u-plane — a different picture with the same number underneath. That is why the limits must move: 0 to 2 in x is a different pair of numbers in u, and the area between them is only the same area if you change both.',
          ),
          text('The table below is worth learning as a habit rather than as a list. In every row the second column is (a constant times) the derivative of the first.'),
          table(['Choose u =', 'because du contains', 'Typical integrand'], [
            ['the inside of a bracket', 'the derivative of that inside', '(3x + 1)⁷ · 3'],
            ['whatever is under a root', 'its derivative', 'x/√(x² + 4)'],
            ['the exponent', 'its derivative', 'x·e^{x²}'],
            ['the denominator', 'its derivative', '(2x + 3)/(x² + 3x)'],
            ['the angle', 'its derivative', 'cos(5x) · 5'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate the definite integral of x(x² + 1)³ from x = 0 to x = 1.'),
          steps([
            { label: 'The composition is (x² + 1)³, so take u to be the inside.', latex: 'u = x^{2} + 1' },
            { label: 'Differentiate, and rearrange for the dx that is actually present.', latex: 'du = 2x\\,dx \\quad\\Longrightarrow\\quad x\\,dx = \\tfrac{1}{2}\\,du', note: 'The integral offers x dx, not 2x dx, so a factor of ½ comes out. That is legal because ½ is a constant.' },
            { label: 'Convert the limits: they are x-values and the variable is about to change.', latex: 'x = 0 \\Rightarrow u = 1, \\qquad x = 1 \\Rightarrow u = 2' },
            { label: 'Rewrite the whole integral. No x survives.', latex: '\\int_{0}^{1} x\\,(x^{2}+1)^{3}\\,dx = \\tfrac{1}{2}\\int_{1}^{2} u^{3}\\,du' },
            { label: 'Now it is the power rule.', latex: '\\tfrac{1}{2}\\left[\\frac{u^{4}}{4}\\right]_{1}^{2} = \\frac{1}{8}\\Big(16 - 1\\Big)' },
            { label: 'Evaluate.', latex: '\\frac{15}{8}' },
          ]),
          insight('Notice what the limit change bought: the answer came out of the u-integral directly, with no need to write (x² + 1)⁴/8 and substitute 1 and 0 into it. On a messy substitution that saves a line; on a trigonometric one it saves a paragraph.'),
          tip('Check any indefinite substitution by differentiating your answer. It takes ten seconds, it uses the chain rule you already have, and it catches a dropped ½ every time.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('u-substitution', 4)] },
      independent: { title: 'On your own', blocks: [practice('u-substitution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('u-substitution', 3, 'Substitutions where the constant has to be borrowed, and definite integrals with limits that move.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Substitution is the chain rule read from right to left.',
            'u is the inside: the bracket, the root, the exponent, the denominator, the angle.',
            'du = g′(x) dx — convert dx, never carry it along unchanged.',
            'A missing constant can be borrowed; a missing factor of x cannot.',
            'For a definite integral, change the limits *or* convert back — one or the other.',
            'Check by differentiating. The chain rule should give the integrand back.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'integration-by-parts-01',
    unitId: 'unit-integration-techniques',
    conceptId: 'integration-by-parts',
    title: 'Integration by Parts',
    summary: 'The product rule rearranged into a trade: one integral for another, and the skill is choosing which.',
    objectives: [
      'Derive the parts formula from the product rule rather than recalling it',
      'Choose u and dv so that the new integral is simpler than the old one',
      'Apply parts twice, and recognise the case where the original integral reappears',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A product that is not a chain',
        blocks: [
          text('Substitution handles an integrand that is a *composition*. It has nothing to say about one that is a plain *product* of two unrelated things:'),
          math('\\int x\\,e^{x}\\,dx'),
          text('Try it and see. Taking u = x gives du = dx and leaves an e^x with nowhere to go. Taking u = e^x gives du = e^x dx, which is not present. Neither factor is the derivative of the other, and substitution has nothing to grip.'),
          insight('There is exactly one differentiation rule left that produces two terms out of a product, and it is the product rule. This lesson is what happens when you integrate both sides of it.'),
        ],
      },
      intuition: {
        title: 'The product rule, rearranged',
        blocks: [
          text('Start from the product rule for two functions u(x) and v(x):'),
          math('(uv)\' = u\'v + uv\''),
          text('Integrate every term with respect to x. The left-hand side integrates immediately — it is a derivative:'),
          math('uv = \\int u\'v\\,dx + \\int uv\'\\,dx'),
          text('Now move one integral to the other side:'),
          math('\\int u\\,v\'\\,dx = uv - \\int u\'\\,v\\,dx'),
          text('In differential notation, with dv = v′ dx and du = u′ dx, that is the form everyone writes:'),
          math('\\int u\\,dv = uv - \\int v\\,du'),
          insight('Read what this actually promises. It does not evaluate the integral. It **trades** it for a different one — and the trade is only worth making if ∫v du is easier than ∫u dv. Parts is a move in a game, not an answer.'),
          text('**Choosing u.** You want the u you differentiate to get *simpler*, and the dv you integrate to be something you actually can integrate. The usual mnemonic orders the candidates for u:'),
          table(['Letter', 'Kind of function', 'Example'], [
            ['L', 'Logarithmic', 'ln x'],
            ['I', 'Inverse trigonometric', 'arctan x'],
            ['A', 'Algebraic', 'x², 3x + 1'],
            ['T', 'Trigonometric', 'sin x, cos x'],
            ['E', 'Exponential', 'e^x, 2^x'],
          ]),
          text('Take u to be whichever appears **earliest** in that list, and dv to be the rest. It is a rule of thumb, not a theorem, and it works because the list is roughly ordered by "gets simpler when differentiated".'),
          warning('If the new integral is worse than the one you started with, you chose u the wrong way round. Swap and try again rather than pressing on — on ∫x e^x dx the wrong choice produces ∫(x²/2)e^x dx, which is a harder problem, and no amount of further work rescues it.', 'A worse integral means the wrong choice'),
          text('**The two special cases** worth meeting immediately:'),
          list([
            'A lone logarithm. ∫ln x dx looks like it has only one factor — but dv can be dx. Take u = ln x, dv = dx, and the formula does the rest.',
            'The circular case. ∫e^x sin x dx returns to itself after applying parts twice. That looks like failure and is not: you get an equation in the unknown integral, and you solve it algebraically.',
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Parts is easiest to run as a two-by-two table, filled in before any integrating happens. Differentiate down the left column, integrate down the right:'),
          table(['', 'differentiate ↓', 'integrate ↓'], [
            ['row 1', 'u = x', 'dv = e^x dx'],
            ['row 2', 'du = dx', 'v = e^x'],
          ]),
          text('The formula then reads straight off the table: uv is (row 1 left) × (row 2 right), and the new integral is ∫(row 2 left) × (row 2 right).'),
          insight('Laying it out this way makes the omission that spoils most attempts visible: v is the *integral* of dv, not dv itself. A blank in the bottom-right corner is the error, and the table has a place for it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find ∫x e^x dx.'),
          steps([
            { label: 'Algebraic beats exponential in LIATE, so u is the x.', latex: 'u = x, \\qquad dv = e^{x}\\,dx' },
            { label: 'Differentiate u; integrate dv.', latex: 'du = dx, \\qquad v = e^{x}' },
            { label: 'Apply the formula.', latex: '\\int x\\,e^{x}\\,dx = x e^{x} - \\int e^{x}\\,dx' },
            { label: 'The traded integral is trivial — which is the whole point.', latex: '= x e^{x} - e^{x} + C' },
            { label: 'Factor, if you like.', latex: '= e^{x}(x - 1) + C' },
          ]),
          insight('Differentiating the answer checks it in one line: (xe^x − e^x)′ = (e^x + xe^x) − e^x = xe^x. That is the product rule doing the reverse of what parts just did, which is the strongest possible sign that the two are the same statement.'),
          text('Now the lone logarithm, which looks impossible until dv = dx is allowed:'),
          steps([
            { label: 'There is nothing else, so dv must be dx.', latex: 'u = \\ln x, \\qquad dv = dx' },
            { label: 'Differentiate and integrate.', latex: 'du = \\frac{1}{x}\\,dx, \\qquad v = x' },
            { label: 'Apply the formula.', latex: '\\int \\ln x\\,dx = x\\ln x - \\int x \\cdot \\frac{1}{x}\\,dx' },
            { label: 'The x cancels, and what is left is dx.', latex: '= x\\ln x - x + C' },
          ]),
          warning('The minus sign in front of the second integral belongs to the whole integral, not just its first term. When ∫v du itself needs a second application of parts, bracket it before going on — an unbracketed minus distributed over two terms is the error that survives every check except differentiating the answer.', 'The minus sign covers everything after it'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('integration-by-parts', 4)] },
      independent: { title: 'On your own', blocks: [practice('integration-by-parts', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('integration-by-parts', 3, 'Definite integrals by parts, and choosing u when both factors look reasonable.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '∫u dv = uv − ∫v du, and it is the product rule rearranged.',
            'Parts trades one integral for another; a worse new integral means u and dv are the wrong way round.',
            'LIATE orders the candidates for u: logarithmic, inverse trig, algebraic, trig, exponential.',
            'v is the integral of dv — the step most often left blank.',
            'For ∫ln x dx, take dv = dx. There is no rule against it.',
            'If the original integral reappears, you have an equation. Solve it.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trigonometric-integrals-01',
    unitId: 'unit-integration-techniques',
    conceptId: 'trigonometric-integrals',
    title: 'Trigonometric Integrals',
    summary: 'Powers of sine and cosine, and the two identities that turn every one of them into a substitution.',
    objectives: [
      'Integrate a product of powers of sine and cosine when one power is odd',
      'Use the half-angle identities when both powers are even',
      'Handle powers of secant and tangent by peeling off sec²x or sec x tan x',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Substitution, blocked by one factor',
        blocks: [
          text('∫sin x cos x dx is a substitution: u = sin x, du = cos x dx, done. So is ∫sin⁵x cos x dx. But this one is not:'),
          math('\\int \\sin^{3}x \\cos^{2}x\\,dx'),
          text('Take u = cos x and du = −sin x dx, and you can account for one of the three sines. Two are left, and they are not part of du. Take u = sin x and the same thing happens with the cosines.'),
          insight('One sine too many is not a dead end, because sin²x is not an independent quantity — the Pythagorean identity converts it into cosines. Every technique in this lesson is that observation applied to a different pair of powers.'),
        ],
      },
      intuition: {
        title: 'Three cases, decided by the parities',
        blocks: [
          text('For ∫sinᵐx cosⁿx dx, look at whether m and n are odd or even. There are only three situations.'),
          text('**Case 1: an odd power of sine.** Peel one sine off and keep it beside dx. The remaining power of sine is even, so it converts entirely into cosines with sin²x = 1 − cos²x. Then u = cos x, du = −sin x dx, and the peeled sine is exactly what du needs.'),
          math('\\sin^{3}x = \\sin^{2}x \\cdot \\sin x = (1 - \\cos^{2}x)\\sin x'),
          text('**Case 2: an odd power of cosine.** The mirror image. Peel one cosine off, convert the rest with cos²x = 1 − sin²x, and substitute u = sin x.'),
          text('**Case 3: both powers even.** Neither peel works — peeling from an even power leaves an odd one, which does not convert. Here the identity you need is not Pythagoras but the half-angle pair, which lowers the powers instead:'),
          math('\\sin^{2}x = \\frac{1 - \\cos 2x}{2}, \\qquad \\cos^{2}x = \\frac{1 + \\cos 2x}{2}'),
          insight('The half-angle identities are the only tool in this lesson that does not reduce to a substitution. They trade a squared function for a plain one at double the angle, and they can be applied repeatedly: cos⁴x becomes a quadratic in cos 2x, whose square term needs the identity a second time.'),
          text('**Secants and tangents** work the same way, with a different pair of derivatives to aim for. Remember which is which:'),
          math('\\frac{d}{dx}\\tan x = \\sec^{2}x, \\qquad \\frac{d}{dx}\\sec x = \\sec x \\tan x'),
          list([
            'An even power of secant: peel off sec²x for du, convert the rest with sec²x = 1 + tan²x, and take u = tan x.',
            'An odd power of tangent: peel off sec x tan x for du, convert the remaining even power of tangent with tan²x = sec²x − 1, and take u = sec x.',
          ]),
          warning('Do not reach for the half-angle identity when a peel is available. ∫sin³x cos²x dx done by half-angles is a page of algebra with three chances to slip; done by peeling it is four lines. Check the parities before writing anything.', 'Check the parities first'),
          tip('Two integrals are worth simply knowing, because they appear constantly and are proved by a substitution you would otherwise redo every time: ∫tan x dx = −ln|cos x| + C, and ∫sec x dx = ln|sec x + tan x| + C.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'cosine',
              transform: { a: 0.5, b: 2, k: 0.5 },
              min: 0,
              max: 6.5,
              label: 'y = cos²x',
            },
            'cos²x, drawn as the half-angle identity describes it: a cosine of double the frequency, halved in height and lifted so it never goes below zero. The average height is ½, which is why ∫cos²x over a whole number of periods is exactly half the width.',
          ),
          insight('That average-height reading is a free check on every even-power integral you will do. Over 0 to π/2, cos²x averages ½ across an interval of width π/2, so the integral is π/4. If your algebra produces π/2 or 1, it is wrong, and you knew before checking a single line.'),
          table(['Integrand', 'What is odd', 'Substitute'], [
            ['sin³x cos²x', 'the sine power', 'u = cos x'],
            ['sin²x cos⁵x', 'the cosine power', 'u = sin x'],
            ['sin²x cos²x', 'neither', 'half-angle identities'],
            ['tan³x sec³x', 'the tangent power', 'u = sec x'],
            ['tan²x sec⁴x', 'the secant power is even', 'u = tan x'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find ∫sin³x cos²x dx.'),
          steps([
            { label: 'The sine power is odd, so peel one sine off.', latex: '\\int \\sin^{2}x \\cos^{2}x \\cdot \\sin x\\,dx' },
            { label: 'Convert the even part into cosines.', latex: '\\int (1 - \\cos^{2}x)\\cos^{2}x \\cdot \\sin x\\,dx' },
            { label: 'Substitute. The peeled sine is exactly du, up to sign.', latex: 'u = \\cos x, \\qquad du = -\\sin x\\,dx' },
            { label: 'Rewrite; the minus comes from du.', latex: '-\\int (1 - u^{2})u^{2}\\,du = -\\int (u^{2} - u^{4})\\,du' },
            { label: 'Power rule.', latex: '-\\frac{u^{3}}{3} + \\frac{u^{5}}{5} + C' },
            { label: 'Put cos x back.', latex: '-\\frac{\\cos^{3}x}{3} + \\frac{\\cos^{5}x}{5} + C' },
          ]),
          text('And the even case, ∫ from 0 to π/2 of cos²x dx:'),
          steps([
            { label: 'Both powers even — use the half-angle identity.', latex: '\\int_{0}^{\\pi/2} \\frac{1 + \\cos 2x}{2}\\,dx' },
            { label: 'Split it.', latex: '\\frac{1}{2}\\int_{0}^{\\pi/2} dx + \\frac{1}{2}\\int_{0}^{\\pi/2}\\cos 2x\\,dx' },
            { label: 'The first is a width; the second needs the 1/2 from the inside derivative.', latex: '\\frac{1}{2}\\cdot\\frac{\\pi}{2} + \\frac{1}{2}\\left[\\frac{\\sin 2x}{2}\\right]_{0}^{\\pi/2}' },
            { label: 'sin π = 0 and sin 0 = 0, so the second term vanishes.', latex: '\\frac{\\pi}{4}' },
          ]),
          insight('π/4 is what the average-height argument predicted before any of that was written. When a technique and a picture agree, you are done checking.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trigonometric-integrals', 4)] },
      independent: { title: 'On your own', blocks: [practice('trigonometric-integrals', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('trigonometric-integrals', 3, 'Mixed parities, secants and tangents, and definite integrals over a quarter period.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Odd power of sine: peel one off, convert the rest with sin²x = 1 − cos²x, take u = cos x.',
            'Odd power of cosine: the mirror image, with u = sin x.',
            'Both even: half-angle identities, sin²x = (1 − cos 2x)/2 and cos²x = (1 + cos 2x)/2.',
            'Even power of secant: peel sec²x, take u = tan x. Odd power of tangent: peel sec x tan x, take u = sec x.',
            'sin²x and cos²x both average ½ — a free plausibility check on every even-power answer.',
            'Check the parities before choosing a method, not after.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trigonometric-substitution-01',
    unitId: 'unit-integration-techniques',
    conceptId: 'trigonometric-substitution',
    title: 'Trigonometric Substitution',
    summary: 'Making a square root vanish by deciding in advance that x is a sine, a tangent or a secant.',
    objectives: [
      'Match a root of the form √(a² − x²), √(a² + x²) or √(x² − a²) to its substitution',
      'Carry the substitution through, including dx and the simplified root',
      'Convert the answer back to x using a right triangle drawn from the substitution',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'A root nothing simplifies',
        blocks: [
          text('Substitution needs the derivative of the inside to be present. In this integral it is not, and there is nothing to peel:'),
          math('\\int \\sqrt{9 - x^{2}}\\,dx'),
          text('The inside is 9 − x², whose derivative is −2x, and there is no x anywhere in the integrand. Parts makes it worse. Nothing in the previous three lessons touches it.'),
          insight('So change tactics completely. Rather than looking for a substitution suggested by the integrand, *impose* one chosen to kill the root — and the identity that kills a difference of two squares under a root is the Pythagorean one, read as 1 − sin²θ = cos²θ.'),
        ],
      },
      intuition: {
        title: 'Three roots, three substitutions',
        blocks: [
          text('Each of the three quadratic forms under a root matches one trigonometric identity, and the substitution is chosen so that the identity applies exactly.'),
          table(['Root', 'Substitute', 'dx becomes', 'The root becomes'], [
            ['√(a² − x²)', 'x = a sin θ', 'a cos θ dθ', 'a cos θ'],
            ['√(a² + x²)', 'x = a tan θ', 'a sec²θ dθ', 'a sec θ'],
            ['√(x² − a²)', 'x = a sec θ', 'a sec θ tan θ dθ', 'a tan θ'],
          ]),
          text('Take the first row and watch the root disappear:'),
          math('\\sqrt{a^{2} - a^{2}\\sin^{2}\\theta} = a\\sqrt{1 - \\sin^{2}\\theta} = a\\sqrt{\\cos^{2}\\theta} = a\\cos\\theta'),
          insight('That is the whole idea. A square root of a quadratic becomes a plain trigonometric function, and the integral that is left is one of the trigonometric integrals from the previous lesson — which is why that lesson comes first. Substituting into ∫√(9 − x²) dx produces ∫9cos²θ dθ, and if you cannot do that, the substitution has not helped you.'),
          text('**Remembering which is which** is easier from the picture than from the table. Draw the right triangle the substitution describes:'),
          list([
            'x = a sin θ means sin θ = x/a: the hypotenuse is a, the opposite side is x, and the adjacent side is √(a² − x²) — the root you wanted.',
            'x = a tan θ means tan θ = x/a: the adjacent side is a, the opposite is x, and the hypotenuse is √(a² + x²).',
            'x = a sec θ means sec θ = x/a: the hypotenuse is x, the adjacent side is a, and the opposite is √(x² − a²).',
          ]),
          text('**Converting back** is the step people forget exists. An answer in θ is not an answer, because the question was asked in x. The triangle above is what converts it: read sin θ, cos θ or tan θ straight off the sides.'),
          warning('θ itself appears in many of these answers — usually from a term that integrated to a plain dθ. That is where the inverse trigonometric function comes in: θ = arcsin(x/a), and it cannot be read off the triangle as a ratio. Leaving a bare θ in a final answer is the commonest way this technique is left unfinished.', 'θ has to go too'),
          tip('For a definite integral, change the limits to θ-values at the start and never convert back. ∫ from 0 to 3 with x = 3 sin θ becomes ∫ from 0 to π/2, the triangle is never needed, and half the work disappears.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'a', height: 'x', hypotenuse: '√(a² + x²)' } },
            'The triangle for x = a tan θ. Every conversion back to x is read off a picture like this one: the substitution fixes two sides, Pythagoras gives the third, and each trigonometric function of θ is then a ratio in x.',
          ),
          figure(
            {
              kind: 'conic',
              conic: 'circle',
              a: 3,
              min: -4,
              max: 4,
              label: 'x² + y² = 9',
            },
            'Why √(9 − x²) turns up at all: it is the upper half of a circle of radius 3. Integrating it from −3 to 3 must therefore give half the disc, 9π/2 — a number you know before you start, and a check on the answer the substitution produces.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find ∫ dx/(x² + 4)^{3/2}.'),
          steps([
            { label: 'The form is a² + x² with a = 2, so x is a tangent.', latex: 'x = 2\\tan\\theta, \\qquad dx = 2\\sec^{2}\\theta\\,d\\theta' },
            { label: 'The bracket becomes a secant squared.', latex: 'x^{2} + 4 = 4\\tan^{2}\\theta + 4 = 4\\sec^{2}\\theta' },
            { label: 'So its three-halves power is a cube.', latex: '(x^{2}+4)^{3/2} = 8\\sec^{3}\\theta' },
            { label: 'Substitute everything.', latex: '\\int \\frac{2\\sec^{2}\\theta}{8\\sec^{3}\\theta}\\,d\\theta = \\frac{1}{4}\\int \\frac{d\\theta}{\\sec\\theta}' },
            { label: 'One over secant is cosine.', latex: '\\frac{1}{4}\\int \\cos\\theta\\,d\\theta = \\frac{1}{4}\\sin\\theta + C' },
            { label: 'Convert back with the triangle: opposite x, adjacent 2, hypotenuse √(x² + 4).', latex: '\\sin\\theta = \\frac{x}{\\sqrt{x^{2}+4}}' },
            { label: 'The answer, in the variable that was asked about.', latex: '\\frac{x}{4\\sqrt{x^{2}+4}} + C' },
          ]),
          insight('Look at what happened to the three-halves power. It was the whole difficulty of the problem, and the substitution turned it into sec³θ, which cancelled against the sec²θ from dx and left a single cosine. Choosing the substitution by the *shape of the root* rather than by the integrand is what makes that cancellation happen.'),
          warning('The final answer must contain no θ and no trigonometric function of θ. If yours does, the triangle step is unfinished — and an answer of ¼ sin θ + C is not merely untidy, it is not a function of x at all.', 'Finish the conversion'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trigonometric-substitution', 4)] },
      independent: { title: 'On your own', blocks: [practice('trigonometric-substitution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('trigonometric-substitution', 3, 'Choosing the substitution, converting back through a triangle, and definite integrals with θ-limits.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '√(a² − x²) → x = a sin θ; √(a² + x²) → x = a tan θ; √(x² − a²) → x = a sec θ.',
            'The point is to kill the root: each substitution turns it into a single trigonometric function.',
            'What is left is a trigonometric integral, so that technique has to be in place first.',
            'Draw the right triangle. It converts every ratio back to x.',
            'A leftover plain θ becomes an inverse trigonometric function, not a ratio.',
            'For a definite integral, change the limits and skip the conversion entirely.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'partial-fractions-01',
    unitId: 'unit-integration-techniques',
    conceptId: 'partial-fractions',
    title: 'Partial Fractions',
    summary: 'Taking a rational function apart into the pieces it was added from, each of which integrates to a logarithm.',
    objectives: [
      'Split a proper rational function with distinct linear factors into partial fractions',
      'Handle a repeated linear factor and an irreducible quadratic factor',
      'Divide first when the numerator has degree at least that of the denominator',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'Undoing an addition',
        blocks: [
          text('You know how to integrate 1/(x − 2). It is ln|x − 2| + C. You know how to integrate 1/(x + 1). What about this?'),
          math('\\int \\frac{5x - 4}{x^{2} - x - 2}\\,dx'),
          text('No substitution works: the derivative of the denominator is 2x − 1, and the numerator is not a multiple of it. But add 2/(x − 2) and 3/(x + 1) together over a common denominator and see what you get.'),
          math('\\frac{2}{x-2} + \\frac{3}{x+1} = \\frac{2(x+1) + 3(x-2)}{(x-2)(x+1)} = \\frac{5x - 4}{x^{2} - x - 2}'),
          insight('The hard integrand is a sum of two easy ones, disguised by having been added up. Partial fractions is the algorithm for running that addition backwards — and once it is undone, every piece is a logarithm.'),
        ],
      },
      intuition: {
        title: 'What shape do the pieces have?',
        blocks: [
          text('**Step zero: is it proper?** The method needs the numerator to have *lower* degree than the denominator. If it does not, divide first — polynomial long division leaves a polynomial plus a proper remainder, and the polynomial part integrates by the power rule.'),
          warning('Skipping this check is the error that produces impossible simultaneous equations. If the degrees are equal, no combination of A/(x − a) + B/(x − b) can ever equal the original, because those pieces tend to 0 far out and the original tends to a non-zero constant. Divide first.', 'Check the degrees before anything else'),
          text('**Step one: factor the denominator.** Every polynomial with real coefficients factors into linear pieces and irreducible quadratics — that is what the fundamental theorem of algebra says once conjugate pairs are multiplied back together. Three kinds of factor can appear, and each has its own shape of piece:'),
          table(['Factor of the denominator', 'Contributes'], [
            ['a distinct linear factor (x − a)', 'A/(x − a)'],
            ['a repeated linear factor (x − a)²', 'A/(x − a) + B/(x − a)²'],
            ['an irreducible quadratic x² + bx + c', '(Ax + B)/(x² + bx + c)'],
          ]),
          insight('The repeated case needs *both* pieces, not just the squared one. Try it with only B/(x − a)² and you get one equation with one unknown and two conditions to satisfy — it fails for almost every numerator. A factor appearing to the power k contributes k terms, with denominators of every power from 1 to k.'),
          text('**Step two: find the constants.** Multiply both sides by the whole denominator to clear the fractions, then either compare coefficients of each power of x, or substitute convenient values of x.'),
          text('**The cover-up shortcut.** For a distinct linear factor (x − a), the constant on top of it is what you get by covering that factor up in the original and putting x = a into everything left:'),
          math('A = \\left.\\frac{5x-4}{x+1}\\right|_{x = 2} = \\frac{6}{3} = 2'),
          tip('Cover-up is the same calculation as substituting x = a into the cleared equation, written down without the intermediate line. It is worth learning *after* the full method, not instead of it — it finds nothing for a repeated factor beyond the highest power, and nothing at all for a quadratic.'),
          text('**Step three: integrate.** Each linear piece is a logarithm; each repeated piece is a power; a quadratic piece splits into a logarithm and an arctangent.'),
          math('\\int\\frac{A}{x-a}\\,dx = A\\ln|x-a| + C, \\qquad \\int\\frac{B}{(x-a)^{2}}\\,dx = \\frac{-B}{x-a} + C'),
          warning('The absolute value in ln|x − a| is not decoration. Without it the antiderivative claims to be undefined for x < a, and the whole branch of the curve to the left of the asymptote becomes unintegrable.', 'Keep the absolute value'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'reciprocal',
              transform: { h: 2 },
              asymptotes: { vertical: [2], horizontal: [0] },
              min: -3,
              max: 6,
              label: 'y = 1/(x − 2)',
            },
            'One partial fraction, drawn. Each piece the method produces is a shifted reciprocal like this, with its own vertical asymptote at its own root of the denominator. The original rational function is these curves added together — which is why it has an asymptote at every root, and why splitting it up is possible at all.',
          ),
          insight('Counting asymptotes is a check on the split. A denominator with three distinct roots produces three pieces, and if your partial-fraction decomposition has two, you missed a factor.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find ∫(5x − 4)/(x² − x − 2) dx.'),
          steps([
            { label: 'Degrees: 1 on top, 2 below. Proper — no division needed.', latex: '\\deg(5x-4) = 1 < 2 = \\deg(x^{2}-x-2)' },
            { label: 'Factor the denominator.', latex: 'x^{2} - x - 2 = (x-2)(x+1)' },
            { label: 'Two distinct linear factors, so two pieces.', latex: '\\frac{5x-4}{(x-2)(x+1)} = \\frac{A}{x-2} + \\frac{B}{x+1}' },
            { label: 'Multiply through by the denominator.', latex: '5x - 4 = A(x+1) + B(x-2)' },
            { label: 'Put x = 2: the B term dies.', latex: '6 = 3A \\quad\\Longrightarrow\\quad A = 2' },
            { label: 'Put x = −1: the A term dies.', latex: '-9 = -3B \\quad\\Longrightarrow\\quad B = 3' },
            { label: 'Integrate the pieces.', latex: '\\int\\frac{2}{x-2}\\,dx + \\int\\frac{3}{x+1}\\,dx' },
            { label: 'Each one is a logarithm.', latex: '2\\ln|x-2| + 3\\ln|x+1| + C' },
          ]),
          insight('The values x = 2 and x = −1 were not chosen to be convenient arithmetic — they were chosen because each kills one unknown. Substituting the roots of the denominator turns a pair of simultaneous equations into two one-line calculations, and it scales: five distinct factors, five substitutions, five constants, no simultaneous equations at all.'),
          tip('Check the split before integrating by putting one more value of x — say x = 0 — into both sides. Here: −4/(−2)(1) = 2 on the left; 2/(−2) + 3/1 = 2 on the right. Thirty seconds, and it catches a sign error before it has been integrated into the answer.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('partial-fractions', 4)] },
      independent: { title: 'On your own', blocks: [practice('partial-fractions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('partial-fractions', 3, 'Repeated factors, improper fractions that need dividing first, and irreducible quadratics.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Check the degrees first. Numerator degree ≥ denominator degree means divide before splitting.',
            'A distinct linear factor gives A/(x − a); a repeated one gives every power from 1 up to k.',
            'An irreducible quadratic gets a linear numerator, (Ax + B), not a constant.',
            'Substituting the roots of the denominator kills one unknown at a time.',
            'Cover-up is that substitution written without the working — and only works on distinct linear factors.',
            'Each linear piece integrates to a logarithm, with the absolute value.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'improper-integrals-01',
    unitId: 'unit-integration-techniques',
    conceptId: 'improper-integrals',
    title: 'Improper Integrals',
    summary: 'Integrating all the way to infinity, or across a point where the function blows up — and the limit that decides whether there is an answer at all.',
    objectives: [
      'Write an improper integral as a limit of proper ones and evaluate it',
      'Decide convergence for the family ∫x^(−p) at infinity and at zero',
      'Split an integral at an interior discontinuity rather than integrating across it',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A wrong answer with nothing wrong with it',
        blocks: [
          text('Here is an evaluation. Every line is a correct application of a rule you have been taught.'),
          math('\\int_{-1}^{1}\\frac{dx}{x} = \\Big[\\ln|x|\\Big]_{-1}^{1} = \\ln 1 - \\ln 1 = 0'),
          warning('The answer is wrong. Not slightly out — the integral does not exist at all. And nothing in those three steps looks suspicious, which is the reason this lesson exists.', 'Every step legal, the answer nonsense'),
          text('What went wrong is that the fundamental theorem has a hypothesis, and the hypothesis was not checked. It requires the integrand to be continuous on the whole closed interval. 1/x is not continuous at 0, which sits in the middle of −1 to 1, and once that fails the theorem promises nothing.'),
          insight('An integral is called **improper** when either the interval is infinitely long or the integrand is unbounded somewhere on it. In both cases the definition of the definite integral — a limit of sums of rectangles over a bounded region — simply does not apply, and something has to be said about what the symbol means before it can be evaluated.'),
        ],
      },
      intuition: {
        title: 'Cut it short, then take a limit',
        blocks: [
          text('The repair is the same in every case: integrate over a region where nothing is wrong, then let the boundary move to the bad place and see whether the answer settles.'),
          text('**Type 1 — an infinite interval.**'),
          math('\\int_{a}^{\\infty} f(x)\\,dx = \\lim_{t \\to \\infty}\\int_{a}^{t} f(x)\\,dx'),
          text('**Type 2 — an infinite discontinuity at an endpoint.** If f blows up at a, keep away from it and then approach:'),
          math('\\int_{a}^{b} f(x)\\,dx = \\lim_{t \\to a^{+}}\\int_{t}^{b} f(x)\\,dx'),
          text('If the limit is a finite number, the integral **converges** and that number is its value. If the limit is infinite or fails to exist, it **diverges**, and the integral has no value — not a value of infinity, no value.'),
          text('**The interior case is the trap.** When the discontinuity is inside the interval, split there and treat both halves separately. The whole converges only if *both* halves do:'),
          math('\\int_{-1}^{1}\\frac{dx}{x} = \\int_{-1}^{0}\\frac{dx}{x} + \\int_{0}^{1}\\frac{dx}{x}'),
          insight('Each half diverges, so the original does. The two infinities do not cancel: cancellation would require them to arrive at the same rate, and there is nothing in the definition that makes them. The bracket evaluation at the top of this lesson was a cancellation performed without permission.'),
          text('**The family that decides everything else.** Both types come down to powers of x, and both answers are worth knowing cold:'),
          table(['Integral', 'Converges when', 'Diverges when'], [
            ['∫ from 1 to ∞ of x^(−p) dx', 'p > 1', 'p ≤ 1'],
            ['∫ from 0 to 1 of x^(−p) dx', 'p < 1', 'p ≥ 1'],
          ]),
          warning('The two rows point opposite ways, and swapping them is the standard error. The reason they differ is the reason the tails matter at one end and the spike matters at the other: far out, a *large* p makes the curve hug the axis fast enough to have finite area; near zero, a large p makes the spike too tall. p = 1 fails at both ends, which is why ∫dx/x is the boundary case in every direction.', 'The two p-tests point opposite ways'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'reciprocal',
              asymptotes: { vertical: [0], horizontal: [0] },
              min: 0,
              max: 6,
              label: 'y = 1/x',
            },
            'The boundary case, from both sides. Running right, the area under 1/x from 1 to t is ln t, which grows without bound — divergent, even though the curve visibly approaches the axis. Running left towards 0, the same thing happens upwards. A curve that gets arbitrarily close to an axis need not enclose a finite area, and this is the picture to keep of that fact.',
          ),
          insight('Compare with 1/x², which lies below 1/x for every x > 1 and does have finite area — exactly 1 — out to infinity. The difference between converging and diverging is not visible in a sketch; it is decided by an exponent, which is why the p-test is worth memorising rather than re-deriving.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate ∫ from 1 to ∞ of dx/x², or show that it diverges.'),
          steps([
            { label: 'Replace ∞ with a variable limit.', latex: '\\lim_{t\\to\\infty}\\int_{1}^{t} x^{-2}\\,dx' },
            { label: 'Antidifferentiate. This part is ordinary calculus.', latex: '\\lim_{t\\to\\infty}\\left[-\\frac{1}{x}\\right]_{1}^{t}' },
            { label: 'Evaluate at both limits.', latex: '\\lim_{t\\to\\infty}\\left(-\\frac{1}{t} + 1\\right)' },
            { label: 'The 1/t term vanishes.', latex: '= 1' },
          ]),
          text('So the region under 1/x² stretching infinitely far to the right has area exactly 1. Now the same integrand from 0 to 1, where the trouble is at the other end:'),
          steps([
            { label: 'The integrand blows up at 0, so approach it.', latex: '\\lim_{t\\to 0^{+}}\\int_{t}^{1} x^{-2}\\,dx' },
            { label: 'Same antiderivative.', latex: '\\lim_{t\\to 0^{+}}\\left[-\\frac{1}{x}\\right]_{t}^{1}' },
            { label: 'Evaluate.', latex: '\\lim_{t\\to 0^{+}}\\left(-1 + \\frac{1}{t}\\right) = \\infty' },
            { label: 'The limit is infinite.', latex: '\\text{divergent}' },
          ]),
          insight('Same function, same rule, opposite verdicts — because p = 2 is greater than 1, which is what convergence needs at infinity and what divergence needs at zero. Write the p down before evaluating and you know the answer in advance; the limit is then a calculation rather than a discovery.'),
          tip('Always write the limit line. Skipping it and jumping to the bracket evaluation is exactly how ∫ from −1 to 1 of dx/x becomes 0: the notation that would have forced you to notice the discontinuity is the notation you left out.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('improper-integrals', 4)] },
      independent: { title: 'On your own', blocks: [practice('improper-integrals', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('improper-integrals', 3, 'Both types, the p-tests in both directions, and integrals that have to be split before anything else happens.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Improper means either an infinite interval or an unbounded integrand.',
            'Replace the bad endpoint with a variable, integrate, then take the limit.',
            'A finite limit means convergent, and that limit is the value; anything else means divergent.',
            'A discontinuity inside the interval must be split at, and both halves must converge.',
            '∫ from 1 to ∞ of x^(−p) converges exactly when p > 1.',
            '∫ from 0 to 1 of x^(−p) converges exactly when p < 1 — the opposite way round.',
          ]),
        ],
      },
    },
  }),
];
